import React, { useState } from "react";
import { Trash2, UserPlus, Loader2 } from "lucide-react";
import { eyeAddFace, eyeDelFace } from "../lib/api";
import { toast } from "sonner";
import { POSES, pose } from "./eyeMath";
import { EyePhotoEnroll } from "./EyePhotoEnroll";

const PER_POSE = 3;
export const OWNER_NAME = "Sohel";

// Guided 5-pose capture; onStep({i,total,label,got}) drives on-screen (and TV) prompts. Returns descriptors.
export const captureFace = async (sample, onStep) => {
  const descs = [];
  for (const [i, ps] of POSES.entries()) {
    let got = 0;
    const until = Date.now() + 9000;
    while (got < PER_POSE && Date.now() < until) {
      onStep({ i, total: POSES.length, label: ps.label, got });
      const r = await sample();
      if (r && ps.ok(pose(r.landmarks))) { descs.push(Array.from(r.descriptor)); got++; }
      await new Promise((ok) => setTimeout(ok, 300));
    }
  }
  return descs;
};

export const EyeFaces = ({ userId, faces, setFaces, sample, ready }) => {
  const [name, setName] = useState("");
  const [role, setRole] = useState(faces.some((f) => f.role === "owner") ? "family" : "owner");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(null);

  const learn = async (nm = name.trim(), rl = role) => {
    if (!nm) return toast.error("Type a name first");
    setBusy(true);
    const descs = await captureFace(sample, setStep);
    setStep(null);
    if (descs.length < 9) { setBusy(false); return toast.error(`Only ${descs.length} good samples. Good light, one face in view, and follow each prompt.`); }
    try {
      const r = await eyeAddFace(userId, nm, rl, descs);
      setFaces(r.faces); setName(""); setRole("family");
      toast.success(`Nexus now knows ${nm} (${descs.length} samples)`);
    } catch (e) { toast.error("Could not save face"); }
    setBusy(false);
  };
  const del = async (n) => { await eyeDelFace(userId, n).catch(() => {}); setFaces(faces.filter((f) => f.name !== n)); };

  return (
    <div className="w-full max-w-md nx-glass rounded-2xl p-4 space-y-3" data-testid="eye-faces-panel">
      <p className="text-xs tracking-[0.3em] text-cyan-300/70 font-bold">FACES NEXUS KNOWS</p>
      {faces.length === 0 && <p className="text-sm text-amber-300/80">Teach Nexus your face first. Until then, nobody is treated as a stranger.</p>}
      {faces.map((f) => (
        <div key={f.name} className="flex items-center justify-between text-sm" data-testid={`eye-face-${f.name}`}>
          <span><b>{f.name}</b> <span className="text-cyan-300/60">· {f.role} · {(f.descriptors || []).length} samples</span></span>
          <button onClick={() => del(f.name)} data-testid={`eye-face-del-${f.name}`} className="text-rose-300/70 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Sohel)" data-testid="eye-face-name"
          className="flex-1 min-w-0 bg-[#030712] border border-cyan-300/20 rounded-lg px-3 py-2 text-sm" />
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="eye-face-role" className="bg-[#030712] border border-cyan-300/20 rounded-lg px-2 text-sm">
          <option value="owner">Me (owner)</option><option value="family">Family</option>
        </select>
      </div>
      {step && (
        <div className="rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-3 text-center" data-testid="eye-enroll-step">
          <p className="text-[11px] text-emerald-200/70">Step {step.i + 1} of {POSES.length} · {step.got}/{PER_POSE}</p>
          <p className="text-base font-bold text-emerald-200">{step.label}</p>
        </div>
      )}
      <EyePhotoEnroll userId={userId} setFaces={setFaces} ownerName={OWNER_NAME} />
      <button onClick={() => learn(OWNER_NAME, "owner")} disabled={!ready || busy} data-testid="eye-teach-owner"
        className="w-full flex items-center justify-center gap-2 bg-[#00F0FF] text-[#030712] rounded-full py-3 font-black disabled:opacity-40">
        <UserPlus className="w-4 h-4" /> Teach Nexus my face ({OWNER_NAME} · owner)
      </button>
      <button onClick={() => learn()} disabled={!ready || busy} data-testid="eye-face-learn"
        className="w-full flex items-center justify-center gap-2 bg-emerald-400 text-[#030712] rounded-full py-2 font-bold disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
        {busy ? "Follow the prompt above…" : ready ? "Learn this face (guided, ~15 samples)" : "Open the eye first"}
      </button>
    </div>
  );
};
